import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Send, Users, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    title: '',
    message: '',
    type: 'announcement',
    link: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/users?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id || !formData.title || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/admin/phase3/notifications`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notification sent successfully!');
      setFormData({
        user_id: '',
        title: '',
        message: '',
        type: 'announcement',
        link: ''
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async () => {
    if (!formData.title || !formData.message) {
      toast.error('Please fill in title and message');
      return;
    }

    if (!window.confirm(`Send notification to all ${users.length} users?`)) {
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('adminToken');
      let successCount = 0;

      // Send to first 50 users (to avoid overwhelming the system)
      const targetUsers = users.slice(0, 50);
      
      for (const user of targetUsers) {
        try {
          await axios.post(
            `${API}/admin/phase3/notifications`,
            { ...formData, user_id: user.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to send to user ${user.id}:`, error);
        }
      }

      toast.success(`Broadcast sent to ${successCount}/${targetUsers.length} users`);
      setFormData({
        user_id: '',
        title: '',
        message: '',
        type: 'announcement',
        link: ''
      });
    } catch (error) {
      console.error('Failed to broadcast:', error);
      toast.error('Failed to complete broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🔔 Notifications Manager
        </h1>
        <p className="text-gray-600">
          Send targeted notifications to users
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{users.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Registered Users</p>
              <p className="text-3xl font-bold text-green-600">
                {users.filter(u => !u.is_anonymous).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Notification Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6">
          Send Notification
        </h2>

        <form onSubmit={handleSendNotification} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient *
            </label>
            <select
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Select a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} {user.email ? `(${user.email})` : '(Anonymous)'} - XP: {user.xp}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="announcement">📢 Announcement</option>
              <option value="achievement">🏆 Achievement</option>
              <option value="streak">🔥 Streak</option>
              <option value="system">⚙️ System</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter notification title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={4}
              placeholder="Write your notification message..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link (Optional)
            </label>
            <input
              type="text"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="/story/scenario-id or external URL"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={sending || !formData.user_id}
              className="flex-1 bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to User
                </>
              )}
            </Button>

            <Button
              type="button"
              onClick={handleBroadcast}
              disabled={sending || !formData.title || !formData.message}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Broadcasting...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Broadcast to All (50 users)
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Tip:</strong> Notifications appear in users&apos; notification center and can include links to scenarios or external resources.
            Broadcast sends to the first 50 users to prevent system overload.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default NotificationsManager;
